import React, { lazy } from 'react';
import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';
import {
  NewLoginLayout,
  LoginLayout,
  MainLayout,
  CustomerLayout,
} from '../views/layouts';
import ROUTES from './routes';

const Login = lazy(() => import('../views/pages/Login'));
const ProjectManager = lazy(() => import('../views/pages/ProjectManager/ProjectManager'));
const ProjectOverview = lazy(() => import('../views/pages/ProjectOverview/ProjectOverview'));
const TimelinePage = lazy(() => import('../views/pages/Timeline/TimelinePage'));
const WriterMatch = lazy(() => import('../views/pages/WriterMatch/WriterMatch'));
const PhotosPage = lazy(() => import('../views/pages/PhotosPage/PhotosPage'));
const AllProjects = lazy(() => import('../views/pages/AllProjects/AllProjects'));
const AllAccounts = lazy(() => import('../views/pages/AllAccounts/AllAccounts'));
const Interviews = lazy(() => import('../views/pages/Interviews/Interviews'));
const ProofPage = lazy(() => import('../views/pages/ProofPage/ProofPage'));
const ChaptersEditor = lazy(() => import('../views/pages/ChaptersEditor/ChaptersEditor'));
const Upgrades = lazy(() => import('../views/pages/Upgrades/Upgrades'));
const CoverCreator = lazy(() => import('../views/pages/CoverCreator/CoverCreator'));
const Versions = lazy(() => import('../views/pages/Versions/Versions'));
const Account = lazy(() => import('../views/pages/Account/Account'));
const EditAccount = lazy(() => import('../views/pages/Account/EditAccount'));
const AddAccount = lazy(() => import('../views/pages/Account/AddAccount'));
const Questionnaire = lazy(() => import('../views/pages/Questionnaire/Questionnaire'));
const Outline = lazy(() => import('../views/pages/Outline/Outline'));
const NoProjectForCustomer = lazy(() => import('../views/pages/NoProjectForCustomer/NoProjectForCustomer'));
const ProjectsFeedback = lazy(() => import('../views/pages/ProjectsFeedback/ProjectsFeedback'));
const QuestionnaireCreator = lazy(() => import('../views/pages/QuestionnaireCreator/QuestionnaireCreator'));
const Logout = lazy(() => import('../views/pages/Logout'));
const NotFound = lazy(() => import('../views/pages/NotFound/NotFound'));
const UpgradesTable = lazy(() => import('../views/pages/UpgradesTable/UpgradesTable'));
const ForgotPassword = lazy(() => import('../views/pages/ForgotPassword/ForgotPassword'));
const PasswordDefine = lazy(() => import('../views/pages/PasswordDefine/PasswordDefine'));
const OnboardingFeedback = lazy(() => import('../views/pages/OnboardingFeedback/OnboardingFeedback'));
const ProofFeedback = lazy(() => import('../views/pages/ProofFeedback/ProofFeedback'));
const RequestNewPassword = lazy(() => import('../views/pages/RequestNewPassword/RequestNewPassword'));
const StyleSamples = lazy(() => import('../views/pages/StyleSamples/StyleSamples'));
const BookSample = lazy(() => import('../views/pages/BookSample/BookSample'));
const PromptLibrary = lazy(() => import('../views/pages/PromptLibrary/PromptLibrary'));
const WriterFeedback = lazy(() => import('../views/pages/WriterFeedbackTable/WriterFeedback'));
const AllUpgrades = lazy(() => import('../views/pages/AllUpgrades'));
const BookSpecification = lazy(() => import('../views/pages/BookSpecification'));

const routesConfig = [
  {
    path: '/login',
    isPrivate: false,
    matcher: PublicRoute,
    layout: NewLoginLayout,
    component: Login,
  },
  {
    path: '/logout',
    isPrivate: false,
    matcher: PublicRoute,
    layout: NewLoginLayout,
    component: Logout,
  },
  {
    path: '/forgot-password',
    isPrivate: false,
    matcher: PublicRoute,
    layout: NewLoginLayout,
    component: ForgotPassword,
  },
  {
    path: '/password-setup/:token',
    isPrivate: false,
    matcher: PublicRoute,
    layout: NewLoginLayout,
    component: props => <PasswordDefine isSetup {...props} />,
  },
  {
    path: '/password-reset/:token',
    isPrivate: false,
    matcher: PublicRoute,
    layout: NewLoginLayout,
    component: PasswordDefine,
  },
  {
    path: '/ask-new-invitation/:token',
    isPrivate: false,
    matcher: PublicRoute,
    layout: NewLoginLayout,
    component: props => <RequestNewPassword isSetup {...props} />,
  },
  {
    path: '/ask-new-invitation/reset/:token',
    isPrivate: false,
    matcher: PublicRoute,
    layout: NewLoginLayout,
    component: RequestNewPassword,
  },
  {
    path: '/projects/create',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ProjectManager,
    translationNamespace: i18next.Namespace.MANAGE_PROJECT,
  },
  {
    path: ROUTES.projects.overview.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ProjectOverview,
    translationNamespace: i18next.Namespace.PROJECT_OVERVIEW,
  },
  {
    path: ROUTES.projects.projectsFeedback.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ProjectsFeedback,
    translationNamespace: i18next.Namespace.PROJECTS_FEEDBACK,
  },
  {
    path: ROUTES.projects.onboardingFeedback.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: OnboardingFeedback,
    translationNamespace: i18next.Namespace.ONBOARDING_FEEDBACK,
  },
  {
    path: ROUTES.projects.proofFeedback.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ProofFeedback,
    translationNamespace: i18next.Namespace.PROOF_FEEDBACK,
  },
  {
    path: ROUTES.projects.writerFeedback.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: WriterFeedback,
    translationNamespace: i18next.Namespace.WRITER_FEEDBACK_TABLE,
  },
  {
    path: '/projects/:projectId/edit',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ProjectManager,
    translationNamespace: i18next.Namespace.MANAGE_PROJECT,
  },
  {
    path: '/projectsW/:projectId/writer-match',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: WriterMatch,
    translationNamespace: i18next.Namespace.WRITER_MATCH,
  },
  {
    path: '/photos',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: PhotosPage,
    translationNamespace: i18next.Namespace.PHOTO_UPLOADER,
  },
  {
    path: '/all-projects',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: AllProjects,
    translationNamespace: i18next.Namespace.ALL_PROJECTS,
  },
  {
    path: '/my-projects',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: AllProjects,
    translationNamespace: i18next.Namespace.ALL_PROJECTS,
  },
  {
    path: '/all-accounts',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: AllAccounts,
    translationNamespace: i18next.Namespace.ALL_ACCOUNTS,
  },
  {
    path: '/timeline',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: TimelinePage,
    translationNamespace: i18next.Namespace.TIMELINE,
  },
  {
    path: ROUTES.projects.interviews.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Interviews,
    translationNamespace: i18next.Namespace.INTERVIEWS,
  },
  {
    path: ROUTES.projects.proof.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ProofPage,
    translationNamespace: i18next.Namespace.PROOF,
  },
  {
    path: '/draft/:index',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ChaptersEditor,
    translationNamespace: i18next.Namespace.DRAFT,
  },
  {
    path: ROUTES.projects.coverCreator.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: CoverCreator,
    translationNamespace: i18next.Namespace.COVER_CREATOR,
  },
  {
    path: ROUTES.projects.upgrades.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Upgrades,
    translationNamespace: i18next.Namespace.UPGRADES,
  },
  {
    path: '/project/:projectId/chapters/:index',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ChaptersEditor,
    translationNamespace: i18next.Namespace.DRAFT,
  },
  {
    path: ROUTES.projects.versions.byProjectId.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Versions,
    translationNamespace: i18next.Namespace.VERSIONS,
  },
  {
    path: '/account',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Account,
    translationNamespace: i18next.Namespace.ACCOUNT_PAGE,
  },
  {
    path: '/account/:userId',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Account,
    translationNamespace: i18next.Namespace.ACCOUNT_PAGE,
  },
  {
    path: '/account/edit/:userId',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: EditAccount,
    translationNamespace: i18next.Namespace.ACCOUNT_PAGE,
  },
  {
    path: '/add-account',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: AddAccount,
    translationNamespace: i18next.Namespace.ACCOUNT_PAGE,
  },
  {
    path: '/questionnaire',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Questionnaire,
    translationNamespace: i18next.Namespace.QUESTIONNAIRE,
  },
  {
    path: '/project/:projectId/outline',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Outline,
    translationNamespace: i18next.Namespace.OUTLINE,
  },
  {
    path: '/outline',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Outline,
    translationNamespace: i18next.Namespace.OUTLINE,
  },
  {
    path: '/project/:projectId/book-specification',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: BookSpecification,
    translationNamespace: i18next.Namespace.BOOK_SPECIFICATION,
  },
  {
    path: '/project/:projectId/book-sample',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: BookSample,
    translationNamespace: i18next.Namespace.BOOK_SAMPLE,
  },
  {
    path: '/book-sample',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: BookSample,
    translationNamespace: i18next.Namespace.BOOK_SAMPLE,
  },
  {
    path: '/project/:projectId/photos',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: PhotosPage,
    translationNamespace: i18next.Namespace.PHOTO_UPLOADER,
  },
  {
    path: ROUTES.projects.overview.byProjectId.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ProjectOverview,
    translationNamespace: i18next.Namespace.PROJECT_OVERVIEW,
  },
  {
    path: ROUTES.projects.proof.byProjectId.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: ProofPage,
    translationNamespace: i18next.Namespace.PROOF,
  },
  {
    path: '/project/:projectId/questionnaire-answers',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Questionnaire,
    translationNamespace: i18next.Namespace.QUESTIONNAIRE,
  },
  {
    path: '/project/:projectId/questionnaire-creator',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: QuestionnaireCreator,
    translationNamespace: i18next.Namespace.QUESTIONNAIRE,
  },
  {
    path: ROUTES.projects.interviews.byProjectId.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Interviews,
    translationNamespace: i18next.Namespace.INTERVIEWS,
  },
  {
    path: ROUTES.projects.coverCreator.byProjectId.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: CoverCreator,
    translationNamespace: i18next.Namespace.COVER_CREATOR,
  },
  {
    path: '/project/:projectId/style-samples',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: StyleSamples,
    translationNamespace: i18next.Namespace.STYLE_SAMPLES,
  },
  {
    path: '/style-samples',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: StyleSamples,
    translationNamespace: i18next.Namespace.STYLE_SAMPLES,
  },
  {
    path: ROUTES.projects.versions.general.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Versions,
    translationNamespace: i18next.Namespace.VERSIONS,
  },
  {
    path: ROUTES.projects.upgrades.byProjectId.path,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: Upgrades,
    translationNamespace: i18next.Namespace.UPGRADES,
  },
  {
    path: '/offline-writer-match',
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: (props) => <WriterMatch isOfflineSearch {...props} />,
    translationNamespace: i18next.Namespace.WRITER_MATCH,
  },
  {
    path: ROUTES.allUpgrades,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: AllUpgrades,
    translationNamespace: i18next.Namespace.ALL_UPGRADES,
  },
  {
    path: ROUTES.upgradesTable,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: UpgradesTable,
    translationNamespace: i18next.Namespace.UPGRADES_TABLE,
  },
  {
    path: ROUTES.promptLibrary,
    isPrivate: true,
    matcher: PrivateRoute,
    layout: MainLayout,
    component: PromptLibrary,
    translationNamespace: i18next.Namespace.PROMPT_LIBRARY,
  },
  {
    path: '/no-project-for-customer',
    isPrivate: false,
    matcher: PublicRoute,
    layout: CustomerLayout,
    component: NoProjectForCustomer,
  },
  {
    path: '/not-found',
    isPrivate: false,
    matcher: PublicRoute,
    layout: LoginLayout,
    component: NotFound,
  },
  {
    path: '*',
    isPrivate: false,
    matcher: PublicRoute,
    layout: LoginLayout,
    component: NotFound,
  },
];

export default routesConfig;
